// @ts-strict-ignore
import { useKeyboardFocus, useUniqueId } from '@usealloy/component-library';
import { datadogRum } from '@datadog/browser-rum';
import type { Action, ThresholdResults } from 'flow/store/engine/types';
import React, {
  useMemo, useRef, useEffect, useContext,
} from 'react';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  setRuleBuilderPanelBoxContainerWidth,
  setRuleBuilderHoveredRuleIndex,
  setSelectedThresholdSimThresholdId,
} from 'flow/store/redux/slice';
import Rule from './Rule/Rule';
import { RuleBuilderContext } from './context';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { StyledActionInfoContainer, StyledRuleBuilderContainer } from './styles';
import { selectThresholds } from 'flow/store/redux/selectors/general';
import { useEditorAccess } from 'flow/editor/context';
import { ContainerType } from '../types';
import EvidencesTable from '~/entities/evaluations/TagRuleModal/EvidencesTable';
import FocusedActionText from 'flow/editor/components/thresholds/FocusedActionText';
import { StyledPre } from 'flow/editor/styles';
import { ActionType } from 'flow/store/engine/constants';
import { selectEvidencesTableProps, selectSelectedThresholdSimThresholdId } from 'flow/store/redux/selectors/workflowFeatures';
import ThresholdSimulatorModal from './ThresholdSimulatorModal';
import { UserContext } from '~/dashboard/context';
import { keyBinds } from './constants';
import MLAFootnotesContainer from 'flow/editor/components/mla/MLAFootnotesContainer';
import RuleClause from './Rule/RuleTemplate/RuleClause';
import TagDescription from '../TagDescription';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors';
import { isActionAnImportedValue, isActionOfType } from 'flow/service/actions';
import MatrixModelContainer from './MatrixModels';
import type { NodeTooltipProps } from 'flow/editor/components/Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';
import { getEditMode } from 'flow/service/general';
import { resetRuleBuilderUiState } from 'flow/store/redux/thunks';
import { ImportedAttributeDetails } from '../../ImportedAttributeDetails';
import { isSnippetChildAction, pruneGateRule } from 'flow/service/actions/snippets';
import {
  buildRuleTree,
  hasMultipleThresholds,
  isAmbiguousGroup,
  mapRulesToIndices,
  mapRuleToDepth,
  testRuleTree,
} from 'flow/service/rules';
import { getMLAsUsedInRules } from 'flow/service/mla';
import RuleBuilderToolbar from './Toolbar';

type RuleBuilderContainerProps = {
  sourceAction: Action;
  containerType: ContainerType;
  ActionHoverTooltip?: (props: NodeTooltipProps) => JSX.Element;
  thresholdResults?: ThresholdResults;
  isPreviewOutcome?: boolean;
};

export default function RuleBuilderContainer({
  sourceAction,
  containerType,
  ActionHoverTooltip,
  thresholdResults,
  isPreviewOutcome,
}: RuleBuilderContainerProps) {
  const dispatch = useAppDispatch();
  const instanceId = useUniqueId('rule-builder');
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const thresholdSimId = useAppSelector(selectSelectedThresholdSimThresholdId);
  const thresholds = useAppSelector(selectThresholds);
  const evidencesTableProps = useAppSelector(
    state => selectEvidencesTableProps(state, sourceAction),
  );

  const { featureFlags } = useContext(UserContext);
  const { editorMode } = useEditorAccess();
  const editMode = getEditMode({
    editorMode, isSubEditorActive: Boolean(activeSubEditor.thresholdId), action: sourceAction,
  });

  const ruleBuilderContainerRef = useRef<HTMLDivElement>(null);
  const keyboardFocus = useKeyboardFocus({
    containerRef: ruleBuilderContainerRef,
    keyBinds,
    onError: (error, context) => datadogRum.addError(error, context),
  });

  const ruleTree = useMemo(() => {
    if (!sourceAction.rules) return null;
    let { rules } = sourceAction;
    if (isSnippetChildAction(sourceAction)) {
      rules = pruneGateRule({ snippetAction: sourceAction, thresholds });
    }
    return rules ? buildRuleTree(rules) : null;
  }, [sourceAction, thresholds]);
  const ruleIdToIndexMap = useMemo(() => (ruleTree ? mapRulesToIndices(ruleTree) : {}), [ruleTree]);
  const ruleIdToDepthMap = useMemo(() => (ruleTree ? mapRuleToDepth(ruleTree) : {}), [ruleTree]);
  const ruleTreeHasMultipleThresholds = useMemo(() => (
    ruleTree ? hasMultipleThresholds(ruleTree) : false
  ), [ruleTree]);
  const isTreeAmbiguous = useMemo(() => (
    ruleTree ? testRuleTree(ruleTree, isAmbiguousGroup) : false
  ), [ruleTree]);
  const rulesWithMLA = useMemo(() => (
    getMLAsUsedInRules({ sourceAction, thresholds })
  ), [sourceAction, thresholds]);

  const ruleBuilderContext = useMemo(() => ({
    sourceAction,
    editMode,
    containerType,
    keyboardFocus,
    isPreviewOutcome: isPreviewOutcome || false,
    thresholdResults: thresholdResults || null,
    // instanceId isolates multiple ThresholdsTab renderings even for the same action
    instanceId,
    ActionHoverTooltip,
    ruleTree,
    ruleIdToDepthMap,
    ruleIdToIndexMap,
    rulesWithMLA,
    ruleTreeHasMultipleThresholds,
    isTreeAmbiguous,
  }), [
    sourceAction,
    containerType,
    editMode,
    keyboardFocus,
    thresholdResults,
    isPreviewOutcome,
    instanceId,
    ActionHoverTooltip,
    ruleTree,
    ruleIdToDepthMap,
    ruleIdToIndexMap,
    rulesWithMLA,
    ruleTreeHasMultipleThresholds,
    isTreeAmbiguous,
  ]);

  // cleanup ui state when the container is unmounted
  useEffect(
    () => {
      if (containerType === ContainerType.TOOLTIP) return undefined;
      return () => dispatch(resetRuleBuilderUiState(sourceAction.publicId));
    },
    [dispatch, sourceAction.publicId, containerType],
  );

  useEffect(() => {
    if (containerType === ContainerType.TOOLTIP) return undefined;
    if (!ruleBuilderContainerRef.current) return undefined;
    const resizeObserver = new ResizeObserver(() => {
      if (!ruleBuilderContainerRef?.current) return false;

      const { width } = ruleBuilderContainerRef.current.getBoundingClientRect();
      if (width > 1) dispatch(setRuleBuilderPanelBoxContainerWidth(width));
      return width;
    });

    resizeObserver.observe(ruleBuilderContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [dispatch, containerType]);

  if (isActionAnImportedValue(sourceAction)) {
    return <ImportedAttributeDetails action={sourceAction} />;
  }

  const renderActionInfo = () => (
    <StyledActionInfoContainer>
      <StyledPre containerType={containerType}>
        <FocusedActionText
          focusedAction={sourceAction}
          hasRules={Boolean(ruleTree)}
        />
      </StyledPre>
      {
        isActionOfType(sourceAction, ActionType.MATRIX_MODEL) && (
          <MatrixModelContainer />
        )
      }
    </StyledActionInfoContainer>
  );

  if (!ruleTree && !isPreviewOutcome) {
    return (
      <RuleBuilderContext.Provider value={ruleBuilderContext}>
        {renderActionInfo()}
      </RuleBuilderContext.Provider>
    );
  }

  const shouldRenderTagDescription = isActionOfType(sourceAction, ActionType.TAG)
    && sourceAction.tag.description;

  const shouldRenderRuleNumber = [ContainerType.DRAWER, ContainerType.ISLAND]
    .includes(containerType);

  return (
    <>
      {/* @ts-expect-error - DndProvider is not typed */}
      <DndProvider backend={HTML5Backend} context={window}>
        <RuleBuilderContext.Provider value={ruleBuilderContext}>
          <StyledRuleBuilderContainer
            ref={ruleBuilderContainerRef}
            renderLines={shouldRenderRuleNumber}
            tabIndex={0}
          >
            {shouldRenderTagDescription && <TagDescription />}
            {!ruleTree && isPreviewOutcome ? (
              <RuleClause />
            ) : (
              <>
                <span
                  {...(containerType === ContainerType.TOOLTIP
                    ? {}
                    : { onMouseLeave: () => dispatch(setRuleBuilderHoveredRuleIndex(null)) }
                  )}
                >
                  <Rule rule={ruleTree} />
                </span>
                {
                  containerType === ContainerType.DRAWER && renderActionInfo()
                }
                {containerType !== ContainerType.TOOLTIP && (
                  <MLAFootnotesContainer rulesWithMLA={rulesWithMLA} />
                )}
                <RuleBuilderToolbar />
                {evidencesTableProps
                    && containerType === ContainerType.DRAWER
                    && (
                      <EvidencesTable
                        {...evidencesTableProps}
                        isPortfolioEvaluation={Boolean(thresholdResults?.is_portfolio_evaluation)}
                      />
                    )}
              </>
            )}
          </StyledRuleBuilderContainer>
        </RuleBuilderContext.Provider>
      </DndProvider>
      {featureFlags.thresholdSimulator && thresholdSimId && (
        <ThresholdSimulatorModal
          isOpen
          onClose={() => dispatch(setSelectedThresholdSimThresholdId(null))}
        />
      )}
    </>
  );
}
